// UploadPriceList.tsx
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Stack,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Tooltip,
  FormHelperText
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileUploader from '../components/FileUploader';
import DataPreviewTable from '../components/DataPreviewTable';
import { buildEntries } from '../../../../../utils/priceListUpload/buildEntries';
import type { ParsedFileResult } from '../../../../../utils/priceListUpload/types';
import { request } from '../../../../../utils/request';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const steps = ['Upload File', 'Map Columns', 'Review & Import'];

const schemaFields: Array<{ key: string }> = [
  { key: 'partNo' },
  { key: 'productName' },
  { key: 'mrp' },
  { key: 'purchasePrice' },
  { key: 'descriptionText' },
  { key: '__extra' }
];

interface Vendor {
  _id: string;
  vendorName: string;
}

const UploadPriceList: React.FC = () => {
  const [parsed, setParsed] = useState<ParsedFileResult | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [mappedRows, setMappedRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '', action: '' });
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<string>('');
  const [vendorValidationDone, setVendorValidationDone] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);

  // Fetch vendors from API
  const fetchVendors = useCallback(async () => {
    setLoadingVendors(true);
    try {
      const response = await request({
        url: 'vendor/get-all-vendors',
        method: 'get'
      });

      if (response.status === "success") {
        setVendors(response.vendors || []);
      } else {
        toast.error('Failed to fetch vendors');
        setVendors([]);
      }
    } catch (error: any) {
      console.error('Error fetching vendors:', error);
      toast.error(error || 'Error fetching vendors');
      setVendors([]);
    } finally {
      setLoadingVendors(false);
    }
  }, []);

  // Auto-refresh vendors when returning to tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && parsed?.vendorName && !vendorValidationDone) {
        fetchVendors();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [parsed?.vendorName, vendorValidationDone, fetchVendors]);

  // Handle browser back button
  useEffect(() => {
    const handleBackButton = (event: PopStateEvent) => {
      if (parsed && activeStep > 0) {
        event.preventDefault();
        setConfirmDialog({
          open: true,
          type: 'browserBack',
          action: 'browserBack'
        });
      }
    };

    window.addEventListener('popstate', handleBackButton);
    window.history.pushState({ hasData: !!parsed }, '');

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [parsed, activeStep]);

  // Update active step based on state
  useEffect(() => {
    if (!parsed && activeStep !== 0) {
      setActiveStep(0);
    } else if (parsed && activeStep === 0) {
      setActiveStep(1);
    }
  }, [parsed, activeStep]);

  // Auto-mapping logic
  useEffect(() => {
    if (parsed) {
      const auto: Record<string, string> = {};
      parsed.headers.forEach((h) => {
        auto[h] = '';
      });
      setMapping(auto);
    } else {
      setMapping({});
    }
  }, [parsed]);

  // Fetch vendors when component mounts
  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const checkVendor = useCallback((vendorName: string) => {
    const existingVendor = vendors.find(v =>
      v.vendorName?.trim().toLowerCase() === vendorName?.trim().toLowerCase()
    );
    if (existingVendor) {
      setSelectedVendor(existingVendor._id);
      setVendorValidationDone(true);
      toast.success(`Vendor "${vendorName}" found and selected automatically`);
    } else {
      setSelectedVendor('');
      setVendorValidationDone(false);
    }
  }, [vendors]);

  // Check vendor when file is parsed
  useEffect(() => {
    if (parsed?.vendorName) {
      checkVendor(parsed.vendorName);
    }
  }, [parsed?.vendorName, vendors, checkVendor]);

  const handleParsed = useCallback((updatedParsed: ParsedFileResult) => {
    if (parsed && updatedParsed) {
      setConfirmDialog({ open: true, type: 'newUpload', action: 'newUpload' });
      return;
    }
    setParsed(updatedParsed);
  }, [parsed]);

  const handleConfirmAction = useCallback(() => {
    const { type } = confirmDialog;

    if (type === 'backToUpload' || type === 'browserBack' || type === 'newUpload') {
      setParsed(null);
      setMapping({});
      setMappedRows([]);
      setActiveStep(0);
      setSelectedVendor('');
      setVendorValidationDone(false);

      if (type === 'browserBack') {
        window.history.back();
      }
    }

    setConfirmDialog({ open: false, type: '', action: '' });
  }, [confirmDialog]);

  const handleCancelAction = useCallback(() => {
    if (confirmDialog.type === 'browserBack') {
      window.history.pushState({ hasData: !!parsed }, '');
    }
    setConfirmDialog({ open: false, type: '', action: '' });
  }, [confirmDialog, parsed]);

  const handleMappingChange = useCallback((newMapping: Record<string, string>) => {
    setMapping(newMapping);
  }, []);

  const handleDeleteColumn = useCallback((header: string) => {
    if (!parsed) return;

    const idx = parsed.headers.indexOf(header);
    if (idx >= 0) {
      const updatedHeaders = parsed.headers.filter(h => h !== header);
      const updatedRows = parsed.rows.map(row => {
        const newRow = [...row];
        newRow.splice(idx, 1);
        return newRow;
      });

      const updatedParsed = {
        ...parsed,
        headers: updatedHeaders,
        rows: updatedRows,
        totalRows: updatedRows.length
      };

      setParsed(updatedParsed);
      setMapping(prev => {
        const copy = { ...prev };
        delete copy[header];
        return copy;
      });
    }
  }, [parsed]);

  const handleMappedRowsChange = useCallback((rows: any[]) => {
    setMappedRows(rows);
  }, []);

  const handleVendorSelect = (vendorId: string) => {
    setVendorValidationDone(true);
    const selectedVendorName = vendors.find(v => v._id === vendorId)?._id || '';
    setSelectedVendor(selectedVendorName);
    toast.success(`Vendor "${selectedVendorName}" selected`);
  };

  const handleAddVendor = () => {
    if (parsed?.vendorName) {
      const vendorCreationUrl = `/vendors/vendor/addVendor?name=${encodeURIComponent(parsed.vendorName)}`;
      window.open(vendorCreationUrl, '_blank');
      toast.info('Opening vendor creation page in new tab...');
    }
  };

  const handleVendorAdded = async () => {
    await fetchVendors();

    if (parsed?.vendorName) {
      const existingVendor = vendors.find(v =>
        v.vendorName?.trim().toLowerCase() === parsed.vendorName?.trim().toLowerCase()
      );

      if (existingVendor) {
        setSelectedVendor(existingVendor._id);
        setVendorValidationDone(true);
        toast.success(`Vendor "${parsed.vendorName}" detected and selected!`);
      } else {
        toast.info('Vendor not found yet. Please make sure you saved the vendor and try refreshing again.');
      }
    }
  };

  const handleNextStep = useCallback(() => {
    if (activeStep === 1 && !vendorValidationDone) {
      toast.error('Please select or add a vendor before continuing');
      return;
    }
    setActiveStep(prev => prev + 1);
  }, [activeStep, vendorValidationDone]);

  const handlePrevStep = useCallback(() => {
    if (activeStep === 1 && parsed) {
      setConfirmDialog({ open: true, type: 'backToUpload', action: 'backToUpload' });
    } else {
      setActiveStep(prev => prev - 1);
    }
  }, [activeStep, parsed]);

  const validateEntries = (entries: any[]) => {
    const errors: string[] = [];
    entries.forEach((e, i) => {
      if (!e.partNo && !e.productName) {
        errors.push(`Row ${i + 1}: Part number or product name required`);
      }
      if (e.mrp !== undefined && typeof e.mrp === 'string' && isNaN(Number(e.mrp))) {
        errors.push(`Row ${i + 1}: MRP must be numeric`);
      }
      if (e.purchasePrice !== undefined && typeof e.purchasePrice === 'string' && isNaN(Number(e.purchasePrice))) {
        errors.push(`Row ${i + 1}: Purchase price must be numeric`);
      }
      if (e.mrp !== undefined && e.purchasePrice !== undefined && Number(e.purchasePrice) > Number(e.mrp)) {
        errors.push(`Row ${i + 1}: Purchase price cannot exceed MRP`);
      }
    });
    return errors;
  };

  const handleSubmit = async () => {
    // FIX: Check both parsed and selectedVendor
    if (!parsed || !selectedVendor) {
      toast.error("Vendor validation required. Please select a vendor.");
      return;
    }

    setLoading(true);
    try {
      const entries = buildEntries(parsed, mapping);

      // ✅ CRITICAL FIX: Override vendor names with selected vendor
      const selectedVendorObj = vendors.find(v => v._id === selectedVendor);
      if (selectedVendorObj) {
        entries.forEach(entry => {
          entry.vendorName = selectedVendorObj.vendorName; // Override with selected vendor
        });
      }

      const errors = validateEntries(entries);
      if (errors.length > 0) {
        toast.error(`Validation failed: ${errors.slice(0, 5).join('; ')}`);
        setLoading(false);
        return;
      }

      if (entries.length === 0) {
        toast.error("No valid data found to import. Please check your file format.");
        setLoading(false);
        return;
      }

      const batchSize = 200;
      let insertedCount = 0;
      const duplicates: string[] = [];
      const errorsAll: string[] = [];

      for (let i = 0; i < entries.length; i += batchSize) {
        const batch = entries.slice(i, i + batchSize);
        try {
          const res = await request({
            url: 'priceList/create-priceList-bulk',
            method: 'post',
            data: {
              entries: batch,
              vendorId: selectedVendor // Use vendor ID
            }
          });

          // FIX: Correct API response handling
          if (res.success) {
            insertedCount += (res.inserted ?? batch.length);
            if (res.duplicates?.length) duplicates.push(...res.duplicates);
            if (res.errors) errorsAll.push(...res.errors);
          } else {
            errorsAll.push(`Batch ${Math.floor(i / batchSize) + 1}: ${res?.message || 'unknown error'}`);
          }
        } catch (batchError: any) {
          errorsAll.push(`Batch ${Math.floor(i / batchSize) + 1}: ${batchError?.message || 'Batch processing error'}`);
        }
      }

      if (errorsAll.length > 0) {
        console.error("Import errors:", errorsAll);
      }

      if (insertedCount > 0) {
        toast.success(`Successfully imported ${insertedCount} rows. ${duplicates.length} duplicates skipped. ${errorsAll.length} errors encountered.`);
      } else if (errorsAll.length > 0) {
        toast.error(`Import failed: ${errorsAll.slice(0, 3).join('; ')}${errorsAll.length > 3 ? '...' : ''}`);
      } else {
        toast.info("No rows were imported. Please check your data.");
      }

      setParsed(null);
      setMapping({});
      setMappedRows([]);
      setActiveStep(0);
      setSelectedVendor('');
      setVendorValidationDone(false);
    } catch (err: any) {
      console.error("Upload error:", err);
      toast.error(`Upload failed: ${err || 'Unknown error occurred'}`);
    } finally {
      setLoading(false);
    }
  };

  const getStepIcon = (step: number) => {
    switch (step) {
      case 0: return <CloudUploadIcon />;
      case 1: return <DescriptionIcon />;
      case 2: return <TableChartIcon />;
      default: return <PlaylistAddCheckIcon />;
    }
  };

  const mappedColumnsCount = Object.values(mapping).filter(v => v && v !== '' && v !== '__extra').length;

  const handleReset = useCallback(() => {
    setParsed(null);
    setMapping({});
    setMappedRows([]);
    setActiveStep(0);
    setSelectedVendor('');
    setVendorValidationDone(false);
  }, []);

  const handleParsedUpdate = useCallback((updatedParsed: ParsedFileResult) => {
    if (updatedParsed.totalRows === 1 && updatedParsed.rows[0]?.[0]?.includes('No readable text')) {
      toast.warning('PDF file processed but limited text was extracted. For best results, use Excel or CSV format.');
    }
    setParsed(updatedParsed);
  }, []);

  const getDialogTitle = () => {
    switch (confirmDialog.type) {
      case 'backToUpload':
        return 'Go Back to Upload?';
      case 'browserBack':
        return 'Leave This Page?';
      case 'newUpload':
        return 'Upload New File?';
      default:
        return 'Confirmation';
    }
  };

  const getDialogMessage = () => {
    switch (confirmDialog.type) {
      case 'backToUpload':
        return 'Going back to upload will clear all your current mappings and data. Are you sure you want to continue?';
      case 'browserBack':
        return 'You have unsaved changes. Leaving this page will clear all your current mappings and data. Are you sure you want to leave?';
      case 'newUpload':
        return 'You already have a file uploaded. Uploading a new file will replace the current one and clear all mappings. Do you want to continue?';
      default:
        return '';
    }
  };

  // Check if vendor from file exists
  const vendorExists = parsed?.vendorName && vendors.some(v =>
    v.vendorName?.toLowerCase() === parsed.vendorName?.toLowerCase()
  );

  return (
    <Box sx={{ margin: '0 auto' }}>
      <Card elevation={3} sx={{ borderRadius: 1 }}>
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h4"
              component="h1"
              fontWeight="600"
              color="primary.main"
              gutterBottom
            >
              Bulk Price List Upload
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Upload and import your price list data in Excel format
            </Typography>
          </Box>

          {/* Stepper */}
          <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 1 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel StepIconComponent={() => getStepIcon(index)}>
                    <Typography variant="body2" fontWeight="medium">
                      {label}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>

          {/* Step 1: File Upload */}
          {activeStep === 0 && (
            <Paper elevation={0} sx={{ p: 4, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <FileUploader
                onParsed={handleParsed}
                onError={(e) => toast.error(String(e))}
              />
            </Paper>
          )}

          {/* Step 2: Column Mapping */}
          {activeStep === 1 && parsed && (
            <Box>
              {/* Vendor Validation Section */}
              <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 1 }}>
                <Box display="flex" alignItems="center" gap={.5}>
                  <Typography variant="h6">
                    Vendor Validation
                  </Typography>
                  <Tooltip title="Refresh to check validation">
                    <IconButton onClick={() => fetchVendors()}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                {!vendorValidationDone ? (
                  <Box>
                    {parsed.vendorName ? (
                      <>
                        <Alert
                          severity="warning"
                          sx={{ mb: 2 }}
                          action={
                            <Button
                              color="secondary"
                              variant='contained'
                              size="small"
                              onClick={handleAddVendor}
                              startIcon={<AddIcon />}
                            >
                              Add Vendor
                            </Button>
                          }
                        >
                          <strong>New Vendor Detected:</strong> "{parsed.vendorName}" is not in your vendor list.
                          Please add this vendor first to continue.
                        </Alert>

                        {/* Vendor selection dropdown */}
                        <Box sx={{ mb: 2 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Or Select Existing Vendor</InputLabel>
                            <Select
                              value={selectedVendor}
                              label="Or Select Existing Vendor"
                              onChange={(e) => handleVendorSelect(e.target.value)}
                              disabled={loadingVendors}
                            >
                              <MenuItem value="">
                                <em>Select a vendor</em>
                              </MenuItem>
                              {vendors.map((vendor) => (
                                <MenuItem key={vendor._id} value={vendor._id}>
                                  {vendor.vendorName}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormHelperText>
                            Select from existing vendors or add the new vendor above
                          </FormHelperText>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          No vendor name detected in the file. Please select a vendor manually.
                        </Alert>

                        {/* Manual vendor selection for cases without vendor name */}
                        <Box sx={{ mb: 2 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Select Vendor *</InputLabel>
                            <Select
                              value={selectedVendor}
                              label="Select Vendor *"
                              onChange={(e) => handleVendorSelect(e.target.value)}
                              disabled={loadingVendors}
                            >
                              <MenuItem value="">
                                <em>Select a vendor</em>
                              </MenuItem>
                              {vendors.map((vendor) => (
                                <MenuItem key={vendor._id} value={vendor._id}>
                                  {vendor.vendorName}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormHelperText error={!selectedVendor}>
                            {!selectedVendor && 'Please select a vendor to continue'}
                          </FormHelperText>
                        </Box>
                      </>
                    )}

                    {/* Refresh section */}
                    <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        Added the vendor in new tab?
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Button
                          variant="outlined"
                          onClick={handleVendorAdded}
                          startIcon={<OpenInNewIcon />}
                          size="small"
                        >
                          Refresh Vendor List
                        </Button>
                        <Typography variant="caption" color="text.secondary">
                          Click here after saving the vendor in the new tab
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Alert
                    severity="success"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => setVendorValidationDone(false)}
                      >
                        Change
                      </Button>
                    }
                  >
                    <strong>Vendor Validated:</strong> {
                      vendors.find(v => v._id === selectedVendor)?.vendorName ||
                      parsed.vendorName ||
                      'Selected vendor'
                    }
                  </Alert>
                )}
              </Paper>

              <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      File Information
                    </Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Vendor Name:
                        </Typography>
                        <Chip
                          label={parsed.vendorName || 'Not specified'}
                          size="small"
                          color={parsed.vendorName ? (vendorExists ? "success" : "warning") : "default"}
                        />
                        {parsed.vendorName && !vendorExists && (
                          <Chip
                            label="New Vendor"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Total Rows:
                        </Typography>
                        <Chip
                          label={parsed.totalRows}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Columns:
                        </Typography>
                        <Chip
                          label={parsed.headers.length}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Stack>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      Mapping Progress
                    </Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Mapped Columns:
                        </Typography>
                        <Chip
                          label={`${mappedColumnsCount} / ${parsed.headers.length}`}
                          size="small"
                          color={mappedColumnsCount > 0 ? "primary" : "default"}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Unique Fields Used:
                        </Typography>
                        <Chip
                          label={`${mappedColumnsCount} / ${schemaFields.filter(f => f.key && f.key !== '__extra' && f.key !== 'descriptionText').length}`}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Stack>
                  </Box>
                </Box>

                <Box sx={{ mt: 2, p: 2, backgroundColor: 'info.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="info.contrastText">
                    <strong>Mapping Rules:</strong> Each data field (Part Number, Product Name, MRP, etc.) can only be mapped to one column.
                    JSON fields and description text can be mapped multiple times.
                  </Typography>
                </Box>
              </Paper>

              <DataPreviewTable
                parsed={parsed}
                mapping={mapping}
                onMappingChange={handleMappingChange}
                onMappedRowsChange={handleMappedRowsChange}
                onDeleteColumn={handleDeleteColumn}
                onParsedUpdate={handleParsedUpdate}
                selectedVendorName={vendors.find(v => v._id === selectedVendor)?.vendorName || ''}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button variant="outlined" onClick={handlePrevStep}>
                  Back to Upload
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNextStep}
                  disabled={mappedColumnsCount === 0 || !vendorValidationDone}
                >
                  Continue to Review
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 3: Review & Import */}
          {activeStep === 2 && parsed && (
            <Box>
              <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Final Data Review
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Review your mapped data before importing
                </Typography>

                {/* Vendor Info */}
                <Alert severity="info" sx={{ mb: 3 }}>
                  <strong>Vendor:</strong> {vendors.find(v => v._id === selectedVendor)?.vendorName || 'Unknown vendor'}
                </Alert>

                <DataPreviewTable
                  parsed={parsed}
                  mapping={mapping}
                  onMappingChange={handleMappingChange}
                  onMappedRowsChange={handleMappedRowsChange}
                  onDeleteColumn={handleDeleteColumn}
                  onParsedUpdate={handleParsedUpdate}
                  showMapping={false}
                  selectedVendorName={vendors.find(v => v._id === selectedVendor)?.vendorName || ''}
                />
              </Paper>

              <Paper
                elevation={1}
                sx={{
                  p: 3,
                  mb: 3,
                  borderRadius: 1,
                  backgroundColor: alpha('#4CAF50', 0.05),
                  border: 1,
                  borderColor: alpha('#4CAF50', 0.2)
                }}
              >
                <Typography variant="h6" gutterBottom color="success.main">
                  Ready to Import
                </Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2">
                      Vendor:
                    </Typography>
                    <Chip
                      label={vendors.find(v => v._id === selectedVendor)?.vendorName || 'Unknown'}
                      color="success"
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2">
                      Total rows to import:
                    </Typography>
                    <Chip
                      label={mappedRows.length}
                      color="success"
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2">
                      Mapped columns:
                    </Typography>
                    <Chip
                      label={`${mappedColumnsCount} / ${parsed.headers.length}`}
                      color="success"
                      size="small"
                    />
                  </Box>
                </Stack>
              </Paper>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button variant="outlined" onClick={handlePrevStep}>
                  Back to Mapping
                </Button>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleReset}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={loading || !selectedVendor} // FIX: Disable if no vendor selected
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                    sx={{ minWidth: 120 }}
                  >
                    {loading ? 'Importing...' : `Import ${mappedRows.length} Rows`}
                  </Button>
                </Stack>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleCancelAction}>
        <DialogTitle>
          {getDialogTitle()}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {getDialogMessage()}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAction}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmAction}
          >
            {confirmDialog.type === 'browserBack' ? 'Leave Page' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UploadPriceList;